import { Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import { ITPDAInfos, PreferenceContext } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { HelpData, Slide } from "src/app/helpers/helpData";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { environment } from "src/environments/environment";
import { AbstractBackendTablePage } from "../abstract-backend-table-page";
import { ParticipantModalComponent } from "./modals/participants-modal/participant-modal.component";
import { QuestionnaireResponseModalComponent } from "./modals/questionnaire-responses-modal/qr-modal.component";
import { SendInvitesModalComponent } from "./modals/send-invites-modal/send-invites-modal.component";
import { TPDAsDataSource } from "./tpdas.datasource";
import { TPDAsService } from "./tpdas.service";

@Component({
  selector: "app-tpdas-page",
  templateUrl: "./tpdas-page.component.html",
  styleUrls: ["./tpdas-page.component.scss"],
})
export class TpdasPageComponent extends AbstractBackendTablePage<ITPDAInfos> {
  public tpdaBaseUrl: string = environment.tpdaBaseUrl;
  public isDownloading = false;

  constructor(
    protected userService: UserService,
    protected sessionService: SessionService,
    protected healthcareService: HealthcareserviceService,
    protected translateService: TranslateService,
    protected preferenceService: PreferenceService,
    protected responsiveService: ResponsiveService,
    protected responsiveDialog: ResponsiveDialogService,
    private tpdaService: TPDAsService,
    private helpData: HelpData,
    private dialog: MatDialog,
    private snackbar: MatSnackBar
  ) {
    super(preferenceService, responsiveService, userService, sessionService, healthcareService, responsiveDialog, translateService);

    this.availableColumns = ["name", "nbParticipants", "nbResults", "action"];
    this.availableColumnItems = [
      { value: "name", display: "tpda.name", checked: true },
      { value: "nbParticipants", display: "tpda.nbParticipants", checked: true },
      { value: "nbResults", display: "tpda.nbResults", checked: true },
      { value: "action", display: "table.action", checked: true },
    ];
    this.displayedColumns = ["name", "nbParticipants", "nbResults", "action"];
  }

  // -------------- Abstract parent methods ------------------
  protected initDataSource(): void {
    this.dataSource = new TPDAsDataSource(this.tpdaService);
  }
  protected getServices(): string[] {
    return this.isAllServices ? undefined : [this.sessionService?.currentService?.reference];
  }

  protected getHelpSlides(): Slide[] {
    return this.helpData.TPDAsPageHelp;
  }

  protected getPreferenceContext(): PreferenceContext {
    return PreferenceContext.TPDA_PAGE;
  }

  // ---------------- Page logic ----------------------------

  public async showParticipants(tpda: ITPDAInfos): Promise<void> {
    const participants = await this.tpdaService.getTPDAParticipants(tpda.TPDAId);
    this.dialog.open(ParticipantModalComponent, {
      data: { participants },
      disableClose: false,
    });
  }

  public async showQRs(tpda: ITPDAInfos): Promise<void> {
    const QRs = await this.tpdaService.getTPDAQRs(tpda.TPDAId).catch((err) => {
      FileLogger.error("TPDA Page", "getTPDAQRs()", err);
      this.snackbar.open(this.translateService.instant("api.errors.unknowerror"), undefined, {
        duration: 3000,
        horizontalPosition: "center",
        verticalPosition: "bottom",
      });
      return [];
    });
    this.dialog.open(QuestionnaireResponseModalComponent, {
      data: { QRs, TPDAId: tpda.TPDAId },
      disableClose: false,
    });
  }

  public goToLink(tpda: ITPDAInfos): void {
    const url = this.tpdaBaseUrl + tpda.TPDAId + "/information";
    window.open(url, "_blank");
  }

  public sendInvites(tpda: ITPDAInfos): void {
    this.dialog.open(SendInvitesModalComponent, {
      data: { TPDAId: tpda.TPDAId },
      disableClose: false,
    });
  }

  public async downloadConsents(tpda: ITPDAInfos): Promise<void> {
    this.isDownloading = true;
    this.snackbar.open(this.translateService.instant("export.message"), "ok", { duration: 3000 });
    try {
      const blob = await this.tpdaService.getTPDAConsentFormsPDFs(tpda.TPDAId);
      const url = URL.createObjectURL(blob);
      Tools.downloadBlob(blob, url.split("/")[url.split("/").length - 1]);
    } catch (err) {
      FileLogger.error("TPDA Page", "downloadConsents()", err);
      this.snackbar.open(this.translateService.instant("api.errors.unknowerror"), undefined, {
        duration: 3000,
        horizontalPosition: "center",
        verticalPosition: "bottom",
      });
    }
    this.isDownloading = false;
  }

  public async downloadQRs(tpda: ITPDAInfos): Promise<void> {
    this.isDownloading = true;
    this.snackbar.open(this.translateService.instant("export.message"), "ok", { duration: 3000 });
    try {
      const blob = await this.tpdaService.getTPDAQRsPDFs(tpda.TPDAId);
      const url = URL.createObjectURL(blob);
      Tools.downloadBlob(blob, url.split("/")[url.split("/").length - 1]);
    } catch (err) {
      FileLogger.error("TPDA Page", "downloadQRs()", err);
      this.snackbar.open(this.translateService.instant("api.errors.unknowerror"), undefined, {
        duration: 3000,
        horizontalPosition: "center",
        verticalPosition: "bottom",
      });
    }
    this.isDownloading = false;
  }
}
